import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { formatFileSize, getFileIcon, SUPPORTED_FILE_TYPES } from '../../../utilities/fileUtils';
import { useDragAndDrop } from '../../core/SupplierAiAgent/hooks/useDragAndDrop';
import { useFileValidation } from '../../core/SupplierAiAgent/hooks/useFileValidation';
import DocumentIcon from '../../shared/DocumentIcon';
import {
    getSupplierGapDocumentSubmission,
    submitSupplierGapDocuments,
    SupplierGapDocumentSubmissionDetails,
    uploadSupplierGapDocument
} from './api';

import './SupplierGapDocumentSubmissionPage.css';

type PageState = 'loading' | 'available' | 'submitted' | 'unavailable' | 'failed';

interface SelectedSupplierFile {
    id: string;
    file: File;
    fileId?: string;
    fileName: string;
    contentType: string;
    fileSize: number;
    isUploading: boolean;
}

const footerLinks = [
    { label: 'About 3E', href: 'https://www.3eco.com/why-3e/' },
    { label: 'Resource Center', href: 'https://www.3eco.com/resource-center/' },
    { label: 'Solutions', href: 'https://www.3eco.com/3e-solutions/' },
    { label: 'Privacy Policy', href: 'https://www.3eco.com/legal/privacy-policy/' },
    { label: 'Terms of Use', href: 'https://www.3eco.com/legal/termsofuse/' },
    { label: 'Contact 3E', href: 'https://www.3eco.com/contact-us/' }
];

const ShieldIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 2.5 20 6v5.4c0 5-3.3 8.6-8 10.1-4.7-1.5-8-5.1-8-10.1V6l8-3.5Z" />
        <path d="m8.4 12 2.2 2.2 5-5" />
    </svg>
);

const UploadIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 14v4.5A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5V14" />
    </svg>
);

const InformationIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="8.5" />
        <path d="M12 10.5v5M12 7.5h.01" />
    </svg>
);

const CheckIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M20 11.1V12a8 8 0 1 1-4.7-7.3" />
        <path d="m8.5 11.8 2.5 2.5L20 5" />
    </svg>
);

const SupplierGapDocumentSubmissionPage: React.FC = () => {
    const [pageState, setPageState] = useState<PageState>('loading');
    const [details, setDetails] = useState<SupplierGapDocumentSubmissionDetails | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<SelectedSupplierFile[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const accessCode = new URLSearchParams(window.location.search).get('accessCode')?.trim() ?? '';

    useEffect(() => {
        document.title = 'Submit documents | 3E Exchange';

        const loadSubmission = async () => {
            if (accessCode.length === 0) {
                setPageState('unavailable');
                return;
            }

            const result = await getSupplierGapDocumentSubmission(accessCode);
            if (result.status === 'ok') {
                setDetails(result.data);
                setPageState(result.data.isSubmitted ? 'submitted' : 'available');
            } else {
                setPageState(result.status === 'unavailable' ? 'unavailable' : 'failed');
            }
        };

        void loadSubmission();
    }, [accessCode]);

    const addFiles = async (files: File[]) => {
        setErrorMessage(null);
        const existingFileKeys = new Set(
            selectedFiles.map(item => `${item.file.name}|${item.file.size}|${item.file.lastModified}`)
        );
        const newFileItems = files.filter(file => {
            const key = `${file.name}|${file.size}|${file.lastModified}`;
            if (existingFileKeys.has(key)) {
                return false;
            }
            existingFileKeys.add(key);
            return true;
        }).map(file => {
            const item: SelectedSupplierFile = {
                id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                file,
                fileName: file.name,
                contentType: file.type,
                fileSize: file.size,
                isUploading: true
            };
            return item;
        });

        if (newFileItems.length === 0) {
            return;
        }

        setSelectedFiles(currentFiles => [...currentFiles, ...newFileItems]);

        for (const item of newFileItems) {
            const result = await uploadSupplierGapDocument(accessCode, item.file);

            if (result.status === 'ok') {
                setSelectedFiles(currentFiles => currentFiles.map(currentItem =>
                    currentItem.id === item.id
                        ? {
                            ...currentItem,
                            fileId: result.data.fileId,
                            fileName: result.data.fileName,
                            contentType: result.data.contentType,
                            fileSize: result.data.fileSize,
                            isUploading: false
                        }
                        : currentItem
                ));
                continue;
            }

            setSelectedFiles(currentFiles => currentFiles.filter(currentItem => currentItem.id !== item.id));

            if (result.status === 'unavailable') {
                setPageState('unavailable');
                return;
            }

            setErrorMessage(result.status === 'invalid-files'
                ? `“${item.file.name}” could not be uploaded. Check its file type and size.`
                : `“${item.file.name}” could not be uploaded. Please try again.`);
        }
    };

    const { handleFiles, handleFileSelect } = useFileValidation({
        onFilesSelected: addFiles,
        onError: setErrorMessage,
        errorMessage: 'Some files could not be added. Each file must be under 20 MB and use a supported file type.'
    });

    const { isDragActive, dragHandlers } = useDragAndDrop({ onDrop: handleFiles });

    const removeFile = (fileId: string) => {
        setSelectedFiles(files => files.filter(file => file.id !== fileId));
        setErrorMessage(null);
    };

    const submitFiles = async () => {
        const uploadedFileIds = selectedFiles
            .filter(file => !file.isUploading && file.fileId)
            .map(file => file.fileId!);

        if (uploadedFileIds.length === 0
            || uploadedFileIds.length !== selectedFiles.length
            || isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        setErrorMessage(null);

        const result = await submitSupplierGapDocuments(accessCode, uploadedFileIds);
        setIsSubmitting(false);

        if (result.status === 'ok' && result.data.isSubmitted) {
            setDetails(result.data);
            setSelectedFiles([]);
            setPageState('submitted');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        if (result.status === 'unavailable') {
            setPageState('unavailable');
            return;
        }

        setErrorMessage(result.status === 'invalid-files'
            ? 'Some files could not be submitted. Remove any empty, unsupported, or oversized files and try again.'
            : 'We could not submit your files. Please try again.');
    };

    const renderAvailableState = () => (
        <>
            <div className="supplier-gap-submission__request-heading">
                <h1>Upload additional documents</h1>
                <p>
                    {details?.customerName
                        ? <><span className="supplier-gap-submission__request-source">3E, on behalf of <strong>{details.customerName}</strong>,</span> has requested additional information to complete your survey response. Upload supporting documentation, a completed gap report, or both. No account or login is required.</>
                        : <><span className="supplier-gap-submission__request-source">3E</span> has requested additional information to complete your survey response. Upload supporting documentation, a completed gap report, or both. No account or login is required.</>}
                </p>

                <div className="supplier-gap-submission__guidance">
                    <div className="supplier-gap-submission__guidance-row">
                        <span className="supplier-gap-submission__guidance-icon"><DocumentIcon /></span>
                        <div>
                            <h2>What to upload</h2>
                            <p>Upload files that help address the remaining gaps identified in the gap report. This may include supporting documents such as SDSs, declarations, test reports, certificates, specifications, packaging information, or a completed gap report.</p>
                        </div>
                    </div>
                    <div className="supplier-gap-submission__guidance-row">
                        <span className="supplier-gap-submission__guidance-icon"><InformationIcon /></span>
                        <div>
                            <h2>What happens next</h2>
                            <p>3E will process your submission and use the information provided to help complete the remaining survey questions.</p>
                        </div>
                    </div>
                </div>
            </div>

            <section className="supplier-gap-submission__upload-section" aria-labelledby="supplier-upload-title">
                <div
                    className={`supplier-gap-submission__dropzone${isDragActive ? ' is-dragging' : ''}`}
                    role="button"
                    tabIndex={0}
                    aria-label="Select files to upload"
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            fileInputRef.current?.click();
                        }
                    }}
                    {...dragHandlers}
                >
                    <span className="supplier-gap-submission__upload-icon"><UploadIcon /></span>
                    <strong id="supplier-upload-title">Drag &amp; drop files or click to browse</strong>
                    <small>Supported: .pdf, .docx, .xlsx, .csv, .txt, .pptx, .xml, .jpg</small>
                </div>

                <input
                    ref={fileInputRef}
                    type="file"
                    multiple={true}
                    accept={SUPPORTED_FILE_TYPES.join(',')}
                    onChange={handleFileSelect}
                    className="supplier-gap-submission__file-input"
                    tabIndex={-1}
                />

                {errorMessage && (
                    <div className="supplier-gap-submission__error" role="alert">{errorMessage}</div>
                )}

                {selectedFiles.length > 0 && (
                    <div className="supplier-gap-submission__file-list" aria-live="polite">
                        <div className="supplier-gap-submission__file-list-heading">
                            <strong>{selectedFiles.length} {selectedFiles.length === 1 ? 'file' : 'files'} selected</strong>
                            <button
                                type="button"
                                disabled={selectedFiles.some(file => file.isUploading)}
                                onClick={() => setSelectedFiles([])}
                            >Clear all</button>
                        </div>
                        {selectedFiles.map(file => (
                            <div className="supplier-gap-submission__file" key={file.id}>
                                <span className={`glyphicon ${getFileIcon(file.fileName)}`} aria-hidden="true" />
                                <span className="supplier-gap-submission__file-details">
                                    <strong title={file.fileName}>{file.fileName}</strong>
                                    <small>
                                        {formatFileSize(file.fileSize)}
                                        {file.isUploading && ' · Uploading…'}
                                    </small>
                                </span>
                                {file.isUploading && <span className="supplier-gap-submission__file-spinner" aria-hidden="true" />}
                                <button
                                    type="button"
                                    disabled={file.isUploading}
                                    onClick={() => removeFile(file.id)}
                                    aria-label={`Remove ${file.fileName}`}
                                >
                                    <span className="glyphicon glyphicon-remove" aria-hidden="true" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <div className="supplier-gap-submission__privacy-note">
                <span className="supplier-gap-submission__shield"><ShieldIcon /></span>
                <p>
                    Your files are submitted through a secure request link and shared only with {details?.customerName
                        ? <><strong>{details.customerName}</strong> and 3E for processing.</>
                        : <>the survey requestor and 3E for processing.</>}
                </p>
            </div>

            <button
                type="button"
                className="supplier-gap-submission__submit"
                disabled={selectedFiles.length === 0
                    || selectedFiles.some(file => file.isUploading || !file.fileId)
                    || isSubmitting}
                onClick={submitFiles}
            >
                {isSubmitting ? <><span className="supplier-gap-submission__button-spinner" />Submitting files…</> : 'Submit files'}
            </button>
        </>
    );

    const renderSubmittedState = () => (
        <div className="supplier-gap-submission__status-state supplier-gap-submission__status-state--success">
            <span className="supplier-gap-submission__status-icon"><CheckIcon /></span>
            <span className="supplier-gap-submission__eyebrow">Submission received</span>
            <h1>Thank you. Your files have been submitted.</h1>
            <p>
                Your documents were securely received for this survey request. You may now close this page.
            </p>
            {details?.customerName && <strong className="supplier-gap-submission__customer-name">For {details.customerName}</strong>}
        </div>
    );

    const renderUnavailableState = (failed: boolean) => (
        <div className="supplier-gap-submission__status-state">
            <span className="supplier-gap-submission__status-icon supplier-gap-submission__status-icon--muted"><ShieldIcon /></span>
            <span className="supplier-gap-submission__eyebrow">Secure document request</span>
            <h1>{failed ? 'We could not load this request' : 'This request is no longer available'}</h1>
            <p>{failed
                ? 'Please refresh the page or try again in a few minutes.'
                : 'The link may be invalid or no longer active. Contact the person who sent it if you still need to provide documents.'}</p>
        </div>
    );

    return (
        <div className="supplier-gap-submission">
            <header className="supplier-gap-submission__header">
                <div className="supplier-gap-submission__shell">
                    <a href="https://exchange.3eco.com/Exchange" aria-label="3E Exchange home">
                        <img src="/images/homepage/3e-exchange-logo-color-rgb.svg" alt="3E Exchange" />
                    </a>
                    <span className="supplier-gap-submission__portal-label">Supplier Document Portal</span>
                </div>
            </header>

            <main className="supplier-gap-submission__main">
                <div className="supplier-gap-submission__card">
                    {pageState === 'loading' && (
                        <div className="supplier-gap-submission__loading" role="status">
                            <span className="supplier-gap-submission__loader" />
                            <p>Loading secure request…</p>
                        </div>
                    )}
                    {pageState === 'available' && renderAvailableState()}
                    {pageState === 'submitted' && renderSubmittedState()}
                    {pageState === 'unavailable' && renderUnavailableState(false)}
                    {pageState === 'failed' && renderUnavailableState(true)}
                </div>
            </main>

            <footer className="supplier-gap-submission__footer">
                <a
                    className="supplier-gap-submission__support-link"
                    href="https://support.3eiq.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <img src="/images/3e-support-light.svg" alt="3E Support" />
                </a>
                <div className="supplier-gap-submission__shell">
                    <nav aria-label="3E company links">
                        {footerLinks.map(link => (
                            <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer">{link.label}</a>
                        ))}
                    </nav>
                    <p>Copyright 3E © {new Date().getFullYear()} · All Rights Reserved</p>
                </div>
            </footer>
        </div>
    );
};

export default SupplierGapDocumentSubmissionPage;
